import { useEffect } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { usePrepareFormTagsForSetReportPayload } from 'hooks/usePrepareFormTagsForSetReportPayload';

import { IContract, IProject, useProjectTreeStore } from 'store/projectContract.store';

import { ReportTemplate } from 'api/endPoints/GetReportTemplateList';

import { optionsToTags } from 'components/Dashboards/Reporting/ReportSettingsModal/components/UpdateReportTags/UpdateReportTags.utils';
import { useProjectContractWithCustomSelectionFunc } from 'components/widgets/ReportsHome/ReportsTemplates/components/CreateReportFromTemplateModal/CreateReportFromTemplateForm/Hooks/ProjectContractWithCustomSelectionFunc';
import { UseCreateReportFromTemplateModalProvider } from 'components/widgets/ReportsHome/ReportsTemplates/components/CreateReportFromTemplateModal/CreateReportFromTemplateModal.provider';
import { FormValues } from 'components/widgets/ReportsHome/ReportsTemplates/components/CreateReportFromTemplateModal/CreateReportFromTemplateModal.types';

export type UseCreateReportFromTemplateModalLogic = ReturnType<typeof useCreateReportFromTemplateModalLogic>;

const NO_TEMPLATE_SELECTED = -1;

interface Props {
    template: ReportTemplate | null;
    api: UseCreateReportFromTemplateModalProvider['api'];
    closeModal: () => void;
}

export const useCreateReportFromTemplateModalLogic = ({ template, api, closeModal }: Props) => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const { getTagsPayloadForReport } = usePrepareFormTagsForSetReportPayload();
    const { handleSnackBar } = useCustomSnackBar();

    const projectFromStore = useProjectTreeStore().project as IProject;
    const contractFromStore = useProjectTreeStore().contract as IContract;

    const form = useForm<FormValues>({
        defaultValues: {
            templateId: template?.id ?? NO_TEMPLATE_SELECTED,
            contract: contractFromStore,
            project: projectFromStore,
            templateName: '',
            selectedTags: [],
        },
    });

    const setContract = (newContract: IContract) => {
        form.setValue('contract', newContract);
    };

    const setProject = (newProject: IProject) => {
        form.setValue('project', newProject);
    };

    const { project, contract, onSelectionFinish } = useProjectContractWithCustomSelectionFunc({
        project: projectFromStore,
        contract: contractFromStore,
        setContract,
        setProject,
    });

    const handleTagsChange = (value: readonly ISelectOption<number>[]) => {
        const selectedTags = optionsToTags(value);
        form.setValue('selectedTags', selectedTags, { shouldDirty: true });
    };

    const hideModal = () => {
        form.reset({
            templateId: NO_TEMPLATE_SELECTED,
            contract: contractFromStore,
            project: projectFromStore,
            templateName: '',
            selectedTags: [],
        });
        closeModal();
    };

    const convertFormValuesToAPIPayload = async (formData: FormValues) => {
        const templateTagsPayload = await getTagsPayloadForReport(formData.selectedTags);
        const apiPayload = {
            templateId: formData.templateId ?? NO_TEMPLATE_SELECTED,
            projectId: formData.project?.id,
            contractId: formData.contract?.id,
            name: formData.templateName,
            tags: templateTagsPayload.map((tag) => tag.id),
        };
        return apiPayload;
    };

    const onNavigateToCreatedReport = (reportId: number): void => {
        const url = AppRoutes.buildPath(AppRoutes.DASHBOARD_REPORTING_PATH, reportId);
        history.push(url);
    };

    const handleSubmitForm = async (data: FormValues) => {
        const { mutateAsync } = api.createReportFromTemplate.instance;
        const payload = await convertFormValuesToAPIPayload(data);
        await mutateAsync(payload, {
            onSuccess: async (createdReport) => {
                hideModal();
                handleSnackBar('Report created successfully from template', 'success');

                await Promise.all([
                    queryClient.invalidateQueries('getReportList'),
                    queryClient.invalidateQueries(['getReportTemplateList']),
                    queryClient.invalidateQueries('getAllReportTags'),
                ]);
                onNavigateToCreatedReport(createdReport.id);
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const contractTitle = useWatch({ control: form.control, name: 'contract.title' });
    const projectTitle = useWatch({ control: form.control, name: 'project.title' });
    const isTemplateNameDirty = form.formState.dirtyFields.templateName;

    useEffect(() => {
        if (template && isTemplateNameDirty !== true) {
            form.reset({
                ...form.getValues(),
                templateName: `${template.name}: ${projectTitle ?? ''} > ${contractTitle ?? ''}`,
                templateId: template.id,
                selectedTags: template.tags,
            });
        }
        if (!template) {
            form.reset({
                ...form.getValues(),
                templateId: NO_TEMPLATE_SELECTED,
            });
        }
    }, [template, projectTitle, contractTitle, isTemplateNameDirty]);

    return {
        logic: {
            form,
            handleTagsChange,
            handleSubmitForm,
            project,
            contract,
            onSelectionFinish,
            hideModal,
        },
    };
};
